<template>
  <template v-if="isVisible">
    <Modal
      :show="true"
      :title="t('Imagens do Serviço')"
      size="small"
      :close-label="t('Fechar')"
      close-test-id="galeria-image-viewer-close-button"
      data-testid="galeria-image-viewer-modal"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <div class="image-viewer">
        <div v-if="currentImage" class="image-container">
          <div class="image-label">
            <h4>{{ currentImage.label }}</h4>
          </div>

          <div class="image-wrapper">
            <img
              :src="currentImage.url"
              :alt="currentImage.label"
              class="full-image"
              data-testid="galeria-image-viewer-full-image"
              @load="handleImageLoad"
              @error="handleImageError"
            />
            <div v-if="imageLoading" class="loading-overlay">
              <ProgressSpinner style="width: 3rem; height: 3rem" strokeWidth="6" />
            </div>
          </div>

          <!-- Navigation buttons -->
          <div v-if="images.length > 1" class="navigation-buttons">
            <button
              type="button"
              class="btn btn-default btn-nav btn-prev"
              data-testid="galeria-image-viewer-previous-button"
              :disabled="currentIndex === 0"
              @click="previousImage"
            >
              <i class="pi pi-chevron-left"></i> {{ t('Anterior') }}
            </button>

            <span class="image-counter">
              {{ currentIndex + 1 }} / {{ images.length }}
            </span>

            <button
              type="button"
              class="btn btn-default btn-nav btn-next"
              data-testid="galeria-image-viewer-next-button"
              :disabled="currentIndex === images.length - 1"
              @click="nextImage"
            >
              {{ t('Próxima') }} <i class="pi pi-chevron-right"></i>
            </button>
          </div>
        </div>

        <div v-else class="no-image">
          <i class="pi pi-image" style="font-size: 3rem;"></i>
          <p>{{ t('Nenhuma imagem disponível') }}</p>
        </div>
      </div>
    </Modal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/ui/Modal.vue';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

interface ViewerImage {
  id?: number | string;
  url: string;
  thumbnailUrl?: string;
  label: string;
  type?: string;
}

interface OpenEventData {
  images?: ViewerImage[];
  initialIndex?: number;
}

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const images = ref<ViewerImage[]>([]);
const initialIndex = ref<number>(0);
const currentIndex = ref<number>(0);
const imageLoading = ref<boolean>(true);

const currentImage = computed<ViewerImage | null>(() => {
  if (images.value.length === 0) return null;
  return images.value[currentIndex.value] || images.value[0];
});

const handleClose = (): void => {
  log('ImageViewerModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  images.value = [];
  currentIndex.value = 0;
};

const previousImage = (): void => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
    imageLoading.value = true;
    log('ImageViewerModal', 'previousImage', 'Navigated to previous image', currentIndex.value);
  }
};

const nextImage = (): void => {
  if (currentIndex.value < images.value.length - 1) {
    currentIndex.value++;
    imageLoading.value = true;
    log('ImageViewerModal', 'nextImage', 'Navigated to next image', currentIndex.value);
  }
};

const handleImageLoad = (): void => {
  imageLoading.value = false;
  log('ImageViewerModal', 'handleImageLoad', 'Image loaded successfully');
};

const handleImageError = (event: Event): void => {
  imageLoading.value = false;
  const target = event.target as HTMLImageElement;
  log('ImageViewerModal', 'handleImageError', 'Error loading image', target.src);
  target.alt = t('Erro ao carregar imagem');
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (!isVisible.value || images.value.length <= 1) return;

  if (event.key === 'ArrowLeft') {
    previousImage();
  } else if (event.key === 'ArrowRight') {
    nextImage();
  } else if (event.key === 'Escape') {
    handleClose();
  }
};

const handleOpen = (data?: OpenEventData): void => {
  log('ImageViewerModal', 'handleOpen', 'Opening modal via eventBus', data);
  images.value = data?.images || [];
  initialIndex.value = data?.initialIndex || 0;
  currentIndex.value = data?.initialIndex || 0;
  imageLoading.value = true;
  isVisible.value = true;
};

onMounted(() => {
  log('ImageViewerModal', 'onMounted', 'Modal mounted, listening for events');

  eventBus.on('modal.imageviewer.open', handleOpen);

  document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  log('ImageViewerModal', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('modal.imageviewer.open', handleOpen);
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.image-viewer {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.image-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.image-label {
  margin-bottom: 15px;
  text-align: center;
}

.image-label h4 {
  margin: 0;
  color: #333;
  font-weight: 600;
}

.image-wrapper {
  position: relative;
  width: 100%;
  max-width: 900px;
  max-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}

.full-image {
  max-width: 100%;
  max-height: 600px;
  height: auto;
  width: auto;
  display: block;
  border-radius: 8px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.loading-overlay i {
  color: #3c8dbc;
}

.navigation-buttons {
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.btn-nav {
  min-width: 120px;
}

.btn-prev, .btn-next {
  font-weight: 600;
}

.image-counter {
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-weight: 600;
  color: #333;
}

.no-image {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.no-image i {
  margin-bottom: 20px;
}

.no-image p {
  font-size: 16px;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .image-wrapper {
    max-height: 400px;
  }

  .full-image {
    max-height: 400px;
  }

  .navigation-buttons {
    flex-direction: column;
    gap: 10px;
  }

  .btn-nav {
    width: 100%;
  }
}
</style>
